import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import {useAuth} from '../../Auth/AuthContext'; 
import styles from '../../Admin/Universidades/Formulario.module.css'; 

const AsociarEstudiantes = ({ abrirModal, cerrarModal }) => {
    const {userData, getToken} = useAuth(); 
    const usuarioId = userData?.id || userData?.usuario_id; 
    const [estudiantesAsociados, setEstudiantesAsociados] = useState([]);
    const [nuevosCorreos, setNuevosCorreos] = useState(['']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [validandoCorreos, setValidandoCorreos] = useState(false);
    const [erroresCorreos, setErroresCorreos] = useState([]);

    const token = getToken(); 
    
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        if (abrirModal && usuarioId) {
            cargarEstudiantesAsociados();
        }
    }, [abrirModal, usuarioId]);

    useEffect(() => {
        if (isClosing) {
            const timer = setTimeout(() => {
                setEstudiantesAsociados([]);
                setNuevosCorreos(['']);
                setError('');
                setErroresCorreos([]);
                cerrarModal();
                setIsClosing(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isClosing, cerrarModal]);

    const cargarEstudiantesAsociados = async () => {
        try {
            setLoading(true);
            setError('');
            
            const response = await fetch(`${process.env.REACT_APP_API_URL}/perfil/estudiantes/${usuarioId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Error al cargar estudiantes asociados');
            }

            const data = await response.json();
            setEstudiantesAsociados(data.estudiantes || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const validarEstudiante = async (correo) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/perfil/validar-estudiante`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ correo })
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Error al validar estudiante');
            }

            return data.estudiante;
        } catch (err) {
            throw new Error(err.message);
        }
    };

    const handleCorreoChange = (index, value) => {
        const nuevosCorreosActualizados = [...nuevosCorreos];
        nuevosCorreosActualizados[index] = value;
        setNuevosCorreos(nuevosCorreosActualizados);
        
        const nuevosErrores = [...erroresCorreos];
        nuevosErrores[index] = '';
        setErroresCorreos(nuevosErrores);
        setError('');
    };

    const eliminarCorreo = (index) => {
        if (nuevosCorreos.length > 1) {
            const nuevosCorreosActualizados = nuevosCorreos.filter((_, i) => i !== index);
            const nuevosErrores = erroresCorreos.filter((_, i) => i !== index);
            setNuevosCorreos(nuevosCorreosActualizados);
            setErroresCorreos(nuevosErrores);
        }
    };

    const validarCorreos = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const nuevosErrores = [];
        let hayErrores = false;

        nuevosCorreos.forEach((correo, index) => {
            if (!correo.trim()) {
                nuevosErrores[index] = 'Por favor ingresa un correo electrónico';
                hayErrores = true;
            } else if (!emailRegex.test(correo)) {
                nuevosErrores[index] = 'Por favor ingresa un correo electrónico válido';
                hayErrores = true;
            } else if (estudiantesAsociados.some(est => est.correo === correo)) {
                nuevosErrores[index] = 'Este estudiante ya está asociado';
                hayErrores = true;
            } else {
                nuevosErrores[index] = '';
            }
        });

        setErroresCorreos(nuevosErrores);
        return !hayErrores;
    };

    const agregarEstudiantes = async () => {
        if (!validarCorreos()) {
            return;
        }

        const correosValidos = nuevosCorreos.filter(correo => correo.trim());
        
        if (correosValidos.length === 0) {
            setError('Por favor ingresa al menos un correo electrónico');
            return;
        }

        try {
            setValidandoCorreos(true);
            setError('');
            
            const nuevosEstudiantes = [];
            
            for (const correo of correosValidos) {
                try {
                    const estudiante = await validarEstudiante(correo);
                    nuevosEstudiantes.push({
                        clave: estudiante.clave,
                        correo: estudiante.correo,
                        nombre: estudiante.nombre,
                        apellido_paterno: estudiante.apellido_paterno,
                        apellido_materno: estudiante.apellido_materno
                    });
                } catch (err) {
                    const index = nuevosCorreos.indexOf(correo);
                    const nuevosErrores = [...erroresCorreos];
                    nuevosErrores[index] = err.message;
                    setErroresCorreos(nuevosErrores);
                }
            }
            
            if (nuevosEstudiantes.length > 0) {
                const estudiantesActualizados = [...estudiantesAsociados, ...nuevosEstudiantes];
                
                await guardarEnBaseDatos(estudiantesActualizados);
                
                setEstudiantesAsociados(estudiantesActualizados);
                setNuevosCorreos(['']); 
                setErroresCorreos([]);
                
                await Swal.fire({
                    title: `${nuevosEstudiantes.length} estudiante(s) agregado(s) correctamente.`,
                    icon: 'success',
                    confirmButtonText: 'Continuar',
                    confirmButtonColor: '#242b57',
                    customClass: { popup: 'popupbonito' }
                });

            }
            
        } catch (err) {
            setError(err.message);
        } finally {
            setValidandoCorreos(false);
        }
    };

    const guardarEnBaseDatos = async (estudiantes) => {
        try {
            const estudiantesClaves = estudiantes.map(est => est.clave);
            
            //wait realizarPeticion(`${process.env.REACT_APP_API_URL}/actualizar-usuario/${idUsuario}`, {
            //const response = await fetch(`${process.env.REACT_APP_API_URL}/actualizar-usuario/${usuarioId}`, {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/perfil/actualizar-usuario/${usuarioId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    estudiantes: estudiantesClaves
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al guardar cambios');
            }

            const data = await response.json();
            return data;
            
        } catch (err) {
            throw new Error(err.message);
        }
    };

    const removerEstudiante = async (correo) => {
        try {
            const estudiantesActualizados = estudiantesAsociados.filter(est => est.correo !== correo);
            
            await guardarEnBaseDatos(estudiantesActualizados);
            
            setEstudiantesAsociados(estudiantesActualizados);
            
            await Swal.fire({
                title: 'Estudiante removido correctamente.',
                icon: 'success',
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' }
            });
            
        } catch (err) {
            setError(err.message);
        }
    };

    const handleCloseModal = () => {
        setIsClosing(true);
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCloseModal();
        }
    };

    if (!abrirModal && !isClosing) return null;

    return (
        <div className={styles.modalAniadir} onClick={handleOverlayClick}>
            <div className={`${styles.modalScrollableAniadir} ${
                abrirModal && !isClosing ? styles.modalAniadirAnim : isClosing ? styles.modalFadeOut : ''
            }`}>
                    <button onClick={handleCloseModal} type="button" className={styles.closeBtnAniadir}>
                        ×
                    </button>
                    <h2 className={styles.tituloAniadir}>Asociar estudiantes</h2>

                    {error && (
                        <div className={styles.mensajeErrorAniadir}>
                    {error}
                        </div>
                    )}

                    <form className={styles.formUsuario}>

                    <div className={styles.seccionTelefonos}>
                        <label>Estudiantes asociad@s:</label>
                        {estudiantesAsociados.length > 0 ? (
                            estudiantesAsociados.map((estudiante, index) => (
                                <div key={index} className={styles.inputGroup}>
                                    <div className={styles.inputWrapper}>
                                        <div className={styles.telefonoInput}>{estudiante.correo}</div>
                                        <button
                                            type="button"
                                            onClick={() => removerEstudiante(estudiante.correo)}
                                            className={styles.botonEliminar}
                                            disabled={loading}
                                            title="Eliminar estudiante"
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className={styles.helpText}>
                                No hay estudiantes asociados.
                            </p>
                        )}
                    </div>

                        {/* Sección para agregar múltiples estudiantes */}
                        <div className={styles.seccionCorreos}>
                            <label>Agregar estudiantes por correo electrónico:</label>
                            {nuevosCorreos.map((correo, index) => (
                                <div key={index}>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type="email"
                                            value={correo}
                                            onChange={(e) => handleCorreoChange(index, e.target.value)}
                                            placeholder="correo@ejemplo.com"
                                            disabled={validandoCorreos || loading}
                                        />
                                        {nuevosCorreos.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => eliminarCorreo(index)}
                                                className={styles.botonEliminar}
                                                disabled={validandoCorreos || loading}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                    {erroresCorreos[index] && (
                                        <p className={styles.mensajeErrorAniadir}>{erroresCorreos[index]}</p>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={agregarEstudiantes}
                                disabled={validandoCorreos || loading || nuevosCorreos.every(c => !c.trim())}
                                className={styles.botonGuard}
                                style={{ marginTop: '10px' }}
                            >
                                {validandoCorreos ? (
                                    <>
                                        <div className={styles.spinner} />
                                        Validando y guardando estudiantes...
                                    </>
                                ) : (
                                    <>
                                        Agregar estudiante(s)
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
            </div>
        </div>
    );
};

export default AsociarEstudiantes;